/**
 * What a `?doc=` in a share link means (web ticket 12, mobile-app ticket 15).
 *
 * A script in an address, so that a link is enough to hand somebody one. The
 * *reading* half of that rule lives here because three hosts read it and only
 * one of them can write it: a tab has an address bar, and a phone has no address
 * at all — it is handed a URL by the operating system when somebody taps a link.
 * So `page/shared-link.ts` keeps the writer beside its own `history.replaceState`
 * and takes the reader from here.
 *
 * **Written without `URL` or `URLSearchParams`, on purpose.** React Native
 * installs its own stubs for both — `URLSearchParams.get` throws
 * `"URLSearchParams.get is not implemented"` and the `URL` accessors throw one
 * each — so a rule in this package that reached for either would typecheck, pass
 * every test in this repository against Node's real ones, and then fail on the
 * one host that has no browser behind it. `platform-agnostic.test.ts` is where
 * that is held rather than remembered.
 */
import { documentUrlFor, isBareDocId } from "./google-docs-url.js";

/** The one parameter. Settings join it in web ticket 18, which is why the
 * writer over in the web package preserves everything it does not own. */
export const DOC_PARAM = "doc";

/**
 * What an address was asked to load, if anything.
 *
 * A bare document id is expanded to the link it names, because otherwise it
 * would fall through to the paste source and be read as a one-word script — no
 * error, and a prompter with a base64 string on it. Anything else is passed
 * through untouched, to fail as whatever it actually is.
 *
 * Takes the query string rather than the whole address: what is in front of the
 * `?` differs by host — an origin the tab is already served from, a URL the
 * phone was launched with — and none of it changes what a `doc` means.
 */
export function sharedDoc(query: string): string | undefined {
  const value = queryParam(query, DOC_PARAM)?.trim();
  if (!value) return undefined;
  return isBareDocId(value) ? documentUrlFor(value) : value;
}

/**
 * One parameter out of a query string, the first occurrence winning.
 *
 * `URLSearchParams`' own semantics, minus the class: `+` is a space, the rest is
 * percent-decoded, a parameter with no `=` is present and empty, and the first
 * of a repeated name is the answer. A leading `?` is allowed because
 * `window.location.search` carries one and a URL's tail does not.
 */
function queryParam(query: string, name: string): string | undefined {
  for (const pair of query.replace(/^\?/u, "").split("&")) {
    if (pair === "") continue;
    const equals = pair.indexOf("=");
    if (decodeValue(equals === -1 ? pair : pair.slice(0, equals)) !== name) {
      continue;
    }
    return equals === -1 ? "" : decodeValue(pair.slice(equals + 1));
  }
  return undefined;
}

/**
 * A percent-encoded piece, decoded — and left alone rather than thrown over when
 * it is not one.
 *
 * A stray `%` in a URL is somebody's typing rather than an escape, and the whole
 * point of passing an unrecognised `doc` through is that it fails later as
 * whatever it actually is. A throw here would instead be an unhandled rejection
 * inside a mount effect on two hosts.
 */
function decodeValue(part: string): string {
  const spaced = part.replace(/\+/gu, " ");
  try {
    return decodeURIComponent(spaced);
  } catch {
    return spaced;
  }
}
